import { useEffect, useState } from "react"
import { IconPrinter } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useHasOpenBenefitEditors } from "@/components/benefit/benefit-edit-scope"
import { api } from "@/lib/api"
import { buildBenefitPrintPath } from "./benefit-print-path"

export { buildBenefitPrintPath }

/** The parent shows this for authorized HELP-capable files; the API independently enforces access. */
export function BenefitPrintActions({ benefitId, programYears = [] }: { benefitId: string; programYears?: number[] }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selection, setSelection] = useState("")
  const hasOpenEditors = useHasOpenBenefitEditors()
  const years = [...new Set(programYears)].filter(year => Number.isInteger(year) && year >= 1900 && year <= 3000).sort((a, b) => b - a)
  const year = years.length === 1 ? years[0] : years.includes(Number(selection)) ? Number(selection) : undefined
  useEffect(() => { setSelection(""); setError(null) }, [benefitId])

  const print = async () => {
    if (busy || hasOpenEditors || (years.length > 1 && year === undefined)) return
    setBusy(true)
    setError(null)
    try {
      await api.download(buildBenefitPrintPath(benefitId, year), "benefit-help.pdf", "application/pdf")
    } catch (failure) {
      const status = failure instanceof Error && "status" in failure ? failure.status : null
      setError(status === 403 ? "Your account cannot print full-identifier HELP packets."
        : status === 409 ? "This file changed or needs protected historical/review evidence. Reload it and check its workflow before printing."
          : status === 400 || status === 404 ? "No HELP packet is available for this file and selected year."
            : "The HELP packet could not be downloaded. Please try again or contact support.")
    } finally {
      setBusy(false)
    }
  }

  return <div className="flex flex-col items-start gap-2">
    {years.length > 1 && <Select value={year?.toString() ?? ""} disabled={busy || hasOpenEditors} onValueChange={value => { setSelection(value); setError(null) }}>
      <SelectTrigger aria-label="HELP print program year"><SelectValue placeholder="Choose print year" /></SelectTrigger>
      <SelectContent>{years.map(value => <SelectItem key={value} value={String(value)}>{value}</SelectItem>)}</SelectContent>
    </Select>}
    <Button type="button" variant="outline" disabled={busy || hasOpenEditors || (years.length > 1 && year === undefined)}
      aria-label="Print HELP packet" onClick={() => void print()}>
      <IconPrinter className="size-4" />{busy ? "Preparing HELP packet…" : "Print HELP packet"}
    </Button>
    {hasOpenEditors && <p className="text-xs text-muted-foreground">Save or cancel the open editor before printing.</p>}
    <p className="max-w-xs text-xs text-muted-foreground">Uses saved data and calculations. Missing or stale calculation evidence is labelled unavailable.</p>
    {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
  </div>
}
